import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';

export interface ChatEmptyStateProps {
  title: string;
  subtitle?: string;
  /** Questions to start with, one click to ask. */
  starters?: string[];
  onPick?: (starter: string) => void;
  disabled?: boolean;
  /** Above the title. A sparkle by default; pass `null` for none. */
  icon?: React.ReactNode;
}

/** Wide enough for a sentence, narrow enough that it reads as one block. */
const BLOCK_WIDTH = 420;

/**
 * A conversation before its first message: what this chat is for, and a few
 * questions that show what it can answer.
 *
 * Centred in the space it is given, so an empty chat reads as an invitation
 * rather than as content that failed to load. The questions stay
 * left-aligned inside their buttons, where they are read.
 */
export function ChatEmptyState({
  title,
  subtitle,
  starters = [],
  onPick,
  disabled,
  icon = <AutoAwesomeRounded />,
}: ChatEmptyStateProps) {
  return (
    <Box
      sx={{
        my: 'auto',
        mx: 'auto',
        width: '100%',
        maxWidth: BLOCK_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 2,
        py: 1,
      }}
    >
      <Box>
        {icon && (
          <Box
            aria-hidden
            sx={(t) => ({
              display: 'flex',
              justifyContent: 'center',
              mb: 1.5,
              color: t.tokens.brand.primary,
              '& svg': { fontSize: 28 },
            })}
          >
            {icon}
          </Box>
        )}
        <Typography variant="h5" component="h2" sx={{ m: 0 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="body2" sx={(t) => ({ mt: 0.5, color: t.tokens.text.secondary })}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {starters.length > 0 && onPick && (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {starters.map((s) => (
            <ButtonBase
              key={s}
              disabled={disabled}
              onClick={() => onPick(s)}
              sx={(t) => ({
                justifyContent: 'flex-start',
                textAlign: 'left',
                px: 1.5,
                py: 1,
                borderRadius: `${t.ds.radius.md}px`,
                border: `1px solid ${t.tokens.border.default}`,
                bgcolor: t.tokens.surface.raised,
                color: t.tokens.text.primary,
                '&:hover': { borderColor: t.tokens.brand.primarySoft, bgcolor: t.tokens.brand.primarySofter },
                '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
              })}
            >
              <Typography variant="body2" sx={{ color: 'inherit' }}>{s}</Typography>
            </ButtonBase>
          ))}
        </Box>
      )}
    </Box>
  );
}
